// ModernSocialDock — Modern Layout 中右ドック内の メンバー + チャット スタック.
//
// 方針 (docs/_wip/modern-layout.md §9.6 / §9 案B、consult 2026-06-08):
//   - §9 案B の右端チャット専用ドロワー (独立 300px 列) は使い勝手調整で**廃止**し、
//     中身 (メンバー / チャット) を**中右ドックに畳む** (consult 2026-06-08 ユーザ指示)。
//     横幅を取りすぎるドロワー列を無くして canvas を広げるのが目的。
//   - チャット/メンバーは paint UI でなく **社交 chrome** なので機能は再利用してよい
//     (= ChatPanel / MemberPanel は流用 OK)。新 UI の視覚言語に合わせて wrap/restyle。
//   - 縦の収め方: メンバーは shrink-0 で上部固定 + **固定高さ (drag-resize)** つき
//     内部スクロール、チャットを `flex-1 min-h-0` にして残り高さを占有 (入力欄は
//     最下部に常時可視)。
//   - **メンバー⇄チャット間に縦 drag-resize splitter** (consult 2026-06-08
//     「Classic にあった『メンバー欄とチャット欄の間のドラッグリサイズ』も欲しい」)。
//     Classic の grid 境界 splitter (paneSizePreference.ts) と同流儀: pointer capture
//     でリアルタイムに member 欄高さを更新 + clamp、localStorage に永続化。
//     高さ計算 / 動的上限 (チャットを潰さない) は modernSocialDockResize.ts の純粋関数。
//   - **Classic の detach「■→」ボタンは Modern では非表示** (§9.6)。
//     → MemberPanel に `chatDetached={false}` + no-op `onToggleChatDetach` を渡す。
//   - 参加人数は MemberPanel ヘッダの「参加者 (N)」が担う (= 旧ドロワーヘッダ相当)。
//
// engine/protocol には触れない。チャット送信・kick・filter は useSession の口を
// そのまま親 (ModernLayout) から受けて流す。

import type { CustomCursor } from '@oekaki/protocol';
import { useCallback, useMemo, useRef, useState } from 'react';

import {
  createPaneSizePreferenceStore,
  MODERN_MEMBER_HEIGHT_PX_MAX,
  MODERN_MEMBER_HEIGHT_PX_MIN,
  type PaneSizePreferenceStore,
} from '../../../infrastructure/paneSizePreference.js';
import { ChatPanel, type ChatPanelHandle } from '../classic/ChatPanel.js';
import { MemberPanel } from '../classic/MemberPanel.js';
import { memberHeightFromPointer } from './modernSocialDockResize.js';

type ChatViewEntries = React.ComponentProps<typeof ChatPanel>['chat'];
type SystemViewEntries = React.ComponentProps<typeof ChatPanel>['system'];
type SlashPorts = React.ComponentProps<typeof ChatPanel>['slashPorts'];
type Members = React.ComponentProps<typeof MemberPanel>['members'];

export interface ModernSocialDockProps {
  // --- chat ---
  readonly chatRef: React.Ref<ChatPanelHandle>;
  readonly chat: ChatViewEntries;
  readonly system: SystemViewEntries;
  readonly selfUid: string;
  readonly isRom: boolean;
  readonly onSendChat: (text: string) => void;
  readonly slashPorts: SlashPorts;
  readonly fontScaleEm: number;
  // --- members ---
  readonly members: Members;
  readonly selfPermissions: number;
  readonly filteredUids: ReadonlySet<string>;
  readonly onKick: (target: { uid: string; displayName: string }) => void;
  readonly onToggleFilter: (uid: string) => void;
  readonly onMemberDoubleClick: (displayName: string) => void;
  /**
   * custom-cursor-2026-09-19.md §2.5-3: 編集中カーソルの transient state
   * (`SessionView.cursorDrafts`)。`MemberPanel` へそのまま横流しする。
   */
  readonly cursorDrafts?: Readonly<Record<string, CustomCursor>>;
  /**
   * custom-cursor-ux フォローアップ (2026-09-19、作者 staging フィードバック §A-2):
   * 「カーソル」エディタを開く単一の口。旧: ソーシャルドック直近の専用テキストボタン
   * (発見性が低いと指摘され撤去) → 新: MemberPanel の自分の行 (サムネ / 「＋」) へ
   * そのまま横流しする (= 補助入口は ModernToolRail 側に移設).
   */
  readonly onOpenCursorEditor: () => void;
  /**
   * custom-cursor-ux フォローアップ: 一度もカーソルエディタを開いたことが無い端末なら
   * true (= MemberPanel の自分の行「＋」プレースホルダを pulse させる)。
   */
  readonly showCursorEditorHint?: boolean;
  /**
   * 永続化 store の override (= テスト用)。未指定なら localStorage を probe。
   */
  readonly paneSizeStore?: PaneSizePreferenceStore;
}

export const ModernSocialDock = ({
  chatRef,
  chat,
  system,
  selfUid,
  isRom,
  onSendChat,
  slashPorts,
  fontScaleEm,
  members,
  selfPermissions,
  filteredUids,
  onKick,
  onToggleFilter,
  onMemberDoubleClick,
  cursorDrafts,
  onOpenCursorEditor,
  showCursorEditorHint,
  paneSizeStore,
}: ModernSocialDockProps): React.ReactNode => {
  // localStorage 永続 store。props 注入が無ければ probe (= 1 回だけ生成)。
  const store = useMemo(() => paneSizeStore ?? createPaneSizePreferenceStore(), [paneSizeStore]);

  // メンバー欄の現在高さ (px)。初期値は永続値 (= default 220)。
  const [memberHeightPx, setMemberHeightPx] = useState<number>(() =>
    store.getModernMemberHeightPx(),
  );

  // メンバー欄 + splitter + チャットを内包する wrapper。splitter drag で
  // 「member 欄上端 Y」と「利用可能縦寸」を測る anchor に使う。
  const stackRef = useRef<HTMLDivElement>(null);
  const memberBoxRef = useRef<HTMLDivElement>(null);

  const onSplitterPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>): void => {
      const memberBox = memberBoxRef.current;
      const stack = stackRef.current;
      if (memberBox === null || stack === null) return;
      e.currentTarget.setPointerCapture(e.pointerId);
      // member 欄上端 Y を anchor に。drag 中は固定 (= wrapper はスクロールしない)。
      const memberTopY = memberBox.getBoundingClientRect().top;
      // member + splitter + chat に使える縦寸 = stack 全高。
      const availableHeight = stack.getBoundingClientRect().height;
      const move = (ev: PointerEvent): void => {
        setMemberHeightPx(memberHeightFromPointer(ev.clientY, memberTopY, availableHeight));
      };
      const up = (ev: PointerEvent): void => {
        try {
          (ev.target as Element | null)?.releasePointerCapture?.(ev.pointerId);
        } catch {
          /* defensive */
        }
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
        window.removeEventListener('pointercancel', up);
        // drag 終了時に最終値を永続化 (= drag 中の高頻度書き込みを避ける)。
        // read 時に再 clamp されるので、ここでは現 state を渡せば十分。
        setMemberHeightPx((current) => {
          store.setModernMemberHeightPx(current);
          return current;
        });
      };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
      window.addEventListener('pointercancel', up);
    },
    [store],
  );

  return (
    <div ref={stackRef} className="flex min-h-0 flex-1 flex-col gap-1">
      {/* メンバー: 上部固定 (shrink-0) + drag-resize 高さ + 内部スクロール。
          参加人数は MemberPanel ヘッダ「参加者 (N)」が表示する。 */}
      <div
        ref={memberBoxRef}
        className="flex shrink-0 flex-col overflow-hidden rounded border border-[var(--color-border)] bg-[var(--color-bg-soft)]"
        style={{ height: memberHeightPx }}
      >
        <MemberPanel
          members={members}
          selfUid={selfUid}
          selfPermissions={selfPermissions}
          onKick={onKick}
          filteredUids={filteredUids}
          onToggleFilter={onToggleFilter}
          onMemberDoubleClick={onMemberDoubleClick}
          chatDetached={false}
          onToggleChatDetach={() => undefined}
          fontScaleEm={fontScaleEm}
          cursorDrafts={cursorDrafts}
          // custom-cursor-ux フォローアップ (2026-09-19、§A-2): 主導線 = 自分の行.
          onOpenCursorEditor={onOpenCursorEditor}
          showCursorEditorHint={showCursorEditorHint}
        />
      </div>
      {/* 縦 splitter: メンバー欄とチャット欄の境界。Classic の grid 境界 splitter
          (role="separator") 同流儀。drag でメンバー欄高さを変更、チャットは残り高さを
          自動占有 (flex-1)。 */}
      {/* biome-ignore lint/a11y/useSemanticElements: drag-resize splitter は role="separator" 慣用 (hairline 子要素を含むため div). */}
      <div
        role="separator"
        aria-orientation="horizontal"
        aria-label="メンバー欄の高さを変更"
        aria-valuenow={Math.round(memberHeightPx)}
        aria-valuemin={MODERN_MEMBER_HEIGHT_PX_MIN}
        aria-valuemax={MODERN_MEMBER_HEIGHT_PX_MAX}
        tabIndex={0}
        data-testid="modern-social-splitter"
        className="group shrink-0 cursor-ns-resize select-none py-[3px]"
        style={{ touchAction: 'none' }}
        onPointerDown={onSplitterPointerDown}
      >
        {/* hover/active で薄いアクセント線 (hit area 中央の 2px bar)。 */}
        <div className="h-[2px] rounded-full bg-[var(--color-border)] transition-colors group-hover:bg-[var(--color-accent)] group-active:bg-[var(--color-accent)]" />
      </div>
      {/* チャット: 残り高さを占有 (flex-1 min-h-0)。ログは内部スクロール、
          入力欄は最下部に常時可視 (ChatPanel 内部で縦 flex を組む)。 */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded border border-[var(--color-border)] bg-[var(--color-bg-soft)]">
        <ChatPanel
          ref={chatRef}
          chat={chat}
          system={system}
          selfUid={selfUid}
          disabled={isRom}
          onSend={onSendChat}
          fontScaleEm={fontScaleEm}
          slashPorts={slashPorts}
        />
      </div>
    </div>
  );
};
